import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService as Auth0Service } from '@auth0/auth0-angular';
import { GetTokenSilentlyOptions, LogoutOptions, User } from '@auth0/auth0-spa-js';
import { Observable, of } from 'rxjs';
import { ApiService } from '../api.service';
import { FrontendLoggerService } from '../frontend-logger.service';
import { TokenService } from '../token.service';
import { AuthCallbackCoordinator } from './auth-callback.coordinator';
import { AuthFlowOrchestratorService } from './auth-flow-orchestrator.service';
import {
  AuthFlowStateService,
  PendingInviteContext,
  ResolvedAuthFlow,
} from './auth-flow-state.service';
import { AuthLoginHistoryRecorder, LoginType } from './auth-login-history.recorder';
import { AuthPostAuthenticationNavigator } from './auth-post-authentication.navigator';
import { AuthSessionClient } from './auth-session.client';
import { DashboardAccessEvaluator } from './dashboard-access.evaluator';

@Injectable({ providedIn: 'root' })
export class AuthService {
  public readonly isAuthenticated$: Observable<boolean>;
  public readonly isLoading$: Observable<boolean>;

  private readonly sessionClient: AuthSessionClient;
  private readonly postAuthNavigator: AuthPostAuthenticationNavigator;
  private readonly callbackCoordinator: AuthCallbackCoordinator;
  private readonly dashboardAccess: DashboardAccessEvaluator;
  private readonly loginHistory: AuthLoginHistoryRecorder;

  constructor(
    auth0: Auth0Service,
    router: Router,
    apiService: ApiService,
    frontendLogger: FrontendLoggerService,
    tokenService: TokenService,
    authFlowState: AuthFlowStateService,
    authFlowOrchestrator: AuthFlowOrchestratorService,
    injector: Injector
  ) {
    this.isAuthenticated$ = auth0.isAuthenticated$;
    this.isLoading$ = (auth0 as any).isLoading$ || of(false);
    this.sessionClient = new AuthSessionClient(
      auth0,
      frontendLogger,
      tokenService,
      authFlowState,
      injector
    );
    this.postAuthNavigator = new AuthPostAuthenticationNavigator(
      auth0,
      router,
      authFlowState,
      authFlowOrchestrator,
      frontendLogger
    );
    this.dashboardAccess = new DashboardAccessEvaluator(apiService, frontendLogger);
    this.loginHistory = new AuthLoginHistoryRecorder(apiService, frontendLogger);
    this.callbackCoordinator = new AuthCallbackCoordinator(
      this.sessionClient,
      this.postAuthNavigator,
      this.loginHistory,
      frontendLogger
    );
    this.logAuth0Config();
  }

  logAuth0Config(): void {
    this.sessionClient.logConfiguration();
  }

  initAuth(): void {
    this.callbackCoordinator.initialize();
  }

  processAuthCallbackIfNeeded(): void {
    this.callbackCoordinator.processIfNeeded();
  }

  login(target = '/chat'): Observable<void> {
    const resolvedTarget = this.resolvePostAuthTarget(target);
    return this.sessionClient.login(
      resolvedTarget,
      () => this.registerLoginHistory(false, 'failed')
    );
  }

  signup(target = '/wizard'): Observable<void> {
    return this.sessionClient.signup(this.resolvePostAuthTarget(target));
  }

  handleRedirectCallback(): Observable<any> {
    return this.sessionClient.handleRedirectCallback();
  }

  logout(options?: LogoutOptions): void {
    this.sessionClient.logout(options);
  }

  isAuthenticated(): Observable<boolean> {
    return this.sessionClient.isAuthenticated();
  }

  getUserProfile(): Observable<User | null> {
    return this.sessionClient.getUserProfile();
  }

  getAccessToken(options?: GetTokenSilentlyOptions): Observable<string> {
    return this.sessionClient.getAccessToken(options);
  }

  hasDashboardAccess(): Observable<boolean> {
    return this.dashboardAccess.hasAccess(() => this.getUserProfile());
  }


  hasDashboardRole(role: string | null | undefined): boolean {
    return this.dashboardAccess.hasDashboardRole(role);
  }

  registerLoginHistory(
    success: boolean,
    loginType: LoginType = 'interactive'
  ): void {
    this.loginHistory.register(success, loginType, () => this.getUserProfile());
  }

  registerLoginHistoryOnceIfNeeded(): void {
    this.loginHistory.registerOnce(() => this.getUserProfile());
  }

  isWizardCompleted(): Observable<boolean> {
    return this.postAuthNavigator.isWizardCompleted();
  }

  setPendingInviteContext(token: string, email: string): void {
    this.postAuthNavigator.setPendingInviteContext(token, email);
  }

  getPendingInviteContext(): PendingInviteContext | null {
    return this.postAuthNavigator.getPendingInviteContext();
  }

  getPendingInviteTarget(): string | null {
    return this.postAuthNavigator.getPendingInviteTarget();
  }

  resolvePostAuthTarget(defaultTarget: string): string {
    return this.resolveLegacyPostAuthTarget(defaultTarget);
  }

  clearPendingInviteContext(): void {
    this.postAuthNavigator.clearPendingInviteContext();
  }

  setResolvedFlow(flow: ResolvedAuthFlow): void {
    this.postAuthNavigator.setResolvedFlow(flow);
  }

  getResolvedFlow(): ResolvedAuthFlow | null {
    return this.postAuthNavigator.getResolvedFlow();
  }

  clearResolvedFlow(): void {
    this.postAuthNavigator.clearResolvedFlow();
  }


  routeAfterAuthentication(defaultTarget: string, source: string): void {
    this.postAuthNavigator.route(defaultTarget, source);
  }

  resolveLegacyPostAuthTarget(defaultTarget: string): string {
    return this.postAuthNavigator.resolveLegacyTarget(defaultTarget);
  }

  isAuth0CallbackQuery(query: string): boolean {
    return this.postAuthNavigator.isAuth0CallbackQuery(query);
  }

  isProviderOAuthCallbackRoute(): boolean {
    return this.postAuthNavigator.isProviderOAuthCallbackRoute();
  }
}
